import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type {
  TicketTypeCreate,
  TicketTypePatch,
  EventTicketType,
  EventTicketTypeCreate,
  EventTicketTypePatch,
  TicketTypeMeracMapping
} from '../types'

// Ticketing (Feature 4). Standard catalogue is org-level and changes rarely —
// long stale time, mirrors useHazardLibrary. Per-event ticket build is scoped
// by eventId and invalidated on every mutation.

// ---- Standard catalogue ----------------------------------------------------

export function useTicketTypes(opts?: { active?: 'true' | 'false' | 'all' }) {
  return useQuery({
    queryKey: ['ticket-types', opts?.active ?? 'true'],
    queryFn: () => ApiClient.ticketTypes(opts),
    staleTime: 30 * 60_000
  })
}

export function useCreateTicketType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: TicketTypeCreate) => ApiClient.createTicketType(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-types'] })
  })
}

export function usePatchTicketType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: TicketTypePatch }) => ApiClient.patchTicketType(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-types'] })
  })
}

export function useDeleteTicketType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deleteTicketType(id).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-types'] })
  })
}

// ---- Per-event ticket build ------------------------------------------------

export function useEventTicketTypes(eventId: number) {
  return useQuery({
    queryKey: ['event-ticket-types', eventId],
    queryFn: () => ApiClient.eventTicketTypes(eventId)
  })
}

// Resolved ticket pricing for the Product Briefing PDF §6 (configured prices,
// MERAC unit-price fallback). Read-only; open to non-financial users.
export function useBriefingTickets(eventId: number) {
  return useQuery({
    queryKey: ['briefing-tickets', eventId],
    queryFn: () => ApiClient.briefingTickets(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0,
    staleTime: 30_000
  })
}

export function useCreateEventTicketType(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EventTicketTypeCreate) => ApiClient.createEventTicketType(eventId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-ticket-types', eventId] })
  })
}

// Optimistic patch: the per-event grid edits fields inline and builds the
// full-replacement body from the cached row, so if we waited for a refetch the
// `row` prop would be stale and two quick edits would clobber each other. We
// merge the edited fields into the cache immediately (preserving meracMappings
// and sales, which the PATCH response does not carry), roll back on error, and
// do NOT invalidate — the edited fields don't affect the derived sales figures.
export function usePatchEventTicketType(eventId: number) {
  const qc = useQueryClient()
  const key = ['event-ticket-types', eventId]
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: EventTicketTypePatch }) =>
      ApiClient.patchEventTicketType(eventId, id, body),
    onMutate: async ({ id, body }: { id: number; body: EventTicketTypePatch }) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<EventTicketType[]>(key)
      qc.setQueryData<EventTicketType[]>(key, (old) =>
        old ? old.map((r) => r.id === id ? { ...r, ...body } : r) : old
      )
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
    }
  })
}

export function useDeleteEventTicketType(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => ApiClient.deleteEventTicketType(eventId, id).then(() => id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-ticket-types', eventId] })
  })
}

// ---- Phased MERAC matching -------------------------------------------------

export function useTicketMeracCandidates(eventId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['ticket-merac-candidates', eventId],
    queryFn: () => ApiClient.ticketMeracCandidates(eventId),
    enabled,
    staleTime: 5 * 60_000
  })
}

export function useSetTicketMeracMap(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, mappings }: { id: number; mappings: TicketTypeMeracMapping[] }) =>
      ApiClient.setTicketMeracMap(eventId, id, mappings),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-ticket-types', eventId] })
  })
}
